import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

/**
 * Repère de parcours affiché sur les 4 écrans du chapitre 3 (CDC) : recueil
 * du besoin, matching, entretien, signature. La navigation classique répartit
 * ces écrans dans des sections différentes (Entreprises, Pilotage,
 * Recrutement) ; ce fil rappelle qu'ils forment une seule suite d'étapes.
 *
 * Ne prétend pas savoir où en est un besoin ou un candidat précis (aucune
 * donnée ne relie aujourd'hui besoin → positionnement → entretien →
 * signature) : il situe seulement l'écran courant dans la suite, sans coller
 * de statut « fait / pas fait » qui serait inventé.
 */

type EtapeCommerciale = 'besoin' | 'matching' | 'entretien' | 'signature';

const ETAPES: { cle: EtapeCommerciale; ecran: string; route: string; libelle: string }[] = [
  { cle: 'besoin', ecran: 'com-entreprises', route: '/com-entreprises', libelle: 'Besoin entreprise' },
  { cle: 'matching', ecran: 'com-matching', route: '/com-matching', libelle: 'Matching candidat' },
  { cle: 'entretien', ecran: 'com-entretiens', route: '/com-entretiens', libelle: 'Entretien' },
  // La signature se traite dans le dossier, où le document est produit :
  // l'étape reste, son écran a changé.
  { cle: 'signature', ecran: 'adm-dossier', route: '/adm-dossier', libelle: 'Signature' },
];

export function ParcoursCommercial({ etape }: { etape: EtapeCommerciale }): JSX.Element {
  const { peutAcceder } = useAuth();

  return (
    <nav
      aria-label="Étape du parcours entreprise → candidat"
      className="relative flex items-center gap-1 overflow-x-auto rounded-xl border border-gray-100 bg-white px-3 py-2.5 text-xs sm:gap-2"
    >
      {ETAPES.map((entree, index) => {
        const actuelle = entree.cle === etape;
        const accessible = peutAcceder(entree.ecran);
        const contenu = (
          <span
            className={`flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 font-semibold whitespace-nowrap transition ${
              actuelle
                ? 'bg-codialis-mint/15 text-codialis-navy'
                : accessible
                  ? 'text-codialis-gray hover:bg-gray-50 hover:text-codialis-navy'
                  : 'cursor-not-allowed text-gray-300'
            }`}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
                actuelle ? 'bg-codialis-navy text-white' : 'bg-gray-100 text-codialis-gray'
              }`}
            >
              {index + 1}
            </span>
            {entree.libelle}
          </span>
        );

        return (
          <div key={entree.cle} className="flex shrink-0 items-center gap-1 sm:gap-2">
            {index > 0 && (
              <svg
                className="h-3 w-3 shrink-0 text-gray-300"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            )}
            {accessible && !actuelle ? (
              <Link to={entree.route} title={entree.libelle}>
                {contenu}
              </Link>
            ) : (
              <span title={accessible ? entree.libelle : `${entree.libelle} — non accessible à ce profil`}>
                {contenu}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}
